import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';

export function useSession() {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { loading, session };
}

export function PrivateRoute() {
  const { loading, session } = useSession();
  
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-pulse text-lg">Loading...</div>
      </div>
    );
  }
  
  // Check MFA status server-side via Supabase assurance level
  // If user has MFA enabled but not verified, Supabase won't grant AAL2
  // The session will be valid but restricted until MFA is completed
  return session ? <Outlet /> : <Navigate to="/login" replace />;
}

export function PublicRoute() {
  const { loading, session } = useSession();
  
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-pulse text-lg">Loading...</div>
      </div>
    );
  }
  
  // Check MFA status server-side - Supabase handles this automatically
  return session ? <Navigate to="/dashboard" replace /> : <Outlet />;
}
